/**
 * NodeHealthBanner — reusable banner for address health status.
 *
 * Renders inline above tab content in NodeInspector.
 * Can also be used standalone (e.g., in TriageRibbon, Omnibar results).
 *
 * Shows nothing when node is healthy.
 */

import React from 'react';
import { AlertTriangle, AlertCircle, RefreshCw } from 'lucide-react';
import type { NodeHealth } from '../../utils/nodeHealth';

interface NodeHealthBannerProps {
    health: NodeHealth;
    /** Optional retry callback (e.g., re-fetch details) */
    onRetry?: () => void;
    /** Compact mode — single line, no retry button */
    compact?: boolean;
}

export const NodeHealthBanner: React.FC<NodeHealthBannerProps> = ({ health, onRetry, compact }) => {
    if (health.status === 'healthy') return null;

    if (health.status === 'unresolvable') {
        return (
            <div className={`flex items-center gap-1.5 ${compact ? 'px-2 py-1' : 'px-2.5 py-1.5 mb-2'} bg-red-500/10 border border-red-500/30 rounded text-xs shrink-0`}>
                <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                <span className="text-red-600 dark:text-red-400 font-medium flex-1">{health.reason}</span>
                {onRetry && !compact && (
                    <button
                        onClick={onRetry}
                        className="text-red-500 hover:text-red-400 transition-colors"
                        title="Retry address lookup"
                    >
                        <RefreshCw className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    }

    // empty status
    return (
        <div className={`flex items-center gap-1.5 ${compact ? 'px-2 py-1' : 'px-2.5 py-1.5 mb-2'} bg-amber-500/10 border border-amber-500/30 rounded text-xs shrink-0`}>
            <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span className="text-amber-600 dark:text-amber-400 font-medium flex-1">{health.reason}</span>
        </div>
    );
};
